import { snakeToCamel } from '@eventbrite/string-utils';
import { getFieldPrefilledValue } from '../../api/transformations/survey';
import { CheckoutState } from '../../state';
import { getNamespaceValue } from '../utils/selector';
import {
    getInteractiveQuestionsByAttendeeId,
    getQuestionsCollection,
} from './pureSelectors';

/**
 * Returns answers that come directly from the backend. They will be present
 * when dealing with transfers (the survey answers from the original order are
 * returned to ease filling the survey of the resulting order after the
 * transfer).
 *
 * These answers will be also present when user is completing a survey that was
 * only partially filled (for instance, when required questions can be answered
 * after payment, the user might leave those required questions unsanswered but
 * they might have answered some questions already, specially those realted to
 * the buyer information like name, surname or email or required waiver
 * questions).
 */
export const getOriginalAnswersByAttendeeId = (state: CheckoutState) => {
    const interactiveQuestionsByAttendeeId =
        getInteractiveQuestionsByAttendeeId(state);
    const surveyResponsesByAttendeeId = getNamespaceValue(
        state.survey,
        'surveyResponsesByAttendeeId',
        {},
    );

    const existingQuestionsByAttendee = Object.fromEntries(
        Object.entries(interactiveQuestionsByAttendeeId).map(
            ([attendeeId, interactiveQuestions]) => [
                attendeeId,
                new Set(
                    interactiveQuestions.map(({ questionId }) => questionId),
                ),
            ],
        ),
    );

    const questionsCollection = getQuestionsCollection(state);

    return Object.fromEntries(
        Object.entries(surveyResponsesByAttendeeId).map(
            ([attendeeId, attendeeResponses]) => [
                attendeeId,
                Object.fromEntries(
                    Object.entries(attendeeResponses)
                        .filter(
                            ([surveyFieldRawName, surveyFieldValue]) =>
                                surveyFieldValue &&
                                existingQuestionsByAttendee[attendeeId]?.has(
                                    surveyFieldRawName,
                                ),
                        )
                        .map(([surveyFieldRawName, surveyFieldValue]) => [
                            surveyFieldRawName,
                            getFieldPrefilledValue({
                                value: surveyFieldValue,
                                type: questionsCollection[
                                    snakeToCamel(surveyFieldRawName)
                                ].type,
                            } as Parameters<typeof getFieldPrefilledValue>[0]) as string,
                        ]),
                ),
            ],
        ),
    );
};
